class VerticalReviewsLoop extends HTMLElement {
  constructor() {
    super();

    /** @type {HTMLElement[]} */
    this.reviewCards = [];
    /** @type {MediaQueryList | undefined} */
    this.tabletMediaQuery = undefined;
    /** @type {MediaQueryList | undefined} */
    this.mobileMediaQuery = undefined;
    /** @type {(() => void) | undefined} */
    this.viewportChangeHandler = undefined;
    /** @type {number | undefined} */
    this.playbackRateFrame = undefined;
    /** @type {HTMLButtonElement | undefined} */
    this.pauseButton = undefined;
    /** @type {HTMLElement | undefined} */
    this.pauseControl = undefined;
    this.isUserPaused = false;
    this.isPointerPaused = false;
    this.playbackToggleHandler = () => this.toggleUserPaused();
  }

  connectedCallback() {
    if (this.viewportChangeHandler) return;

    const reviewSet = this.querySelector(':scope > .vertical-reviews__set');
    if (!reviewSet) return;

    this.reviewCards = Array.from(
      reviewSet.querySelectorAll('.vertical-reviews__card:not([aria-hidden])')
    ).flatMap((card) => {
      const cardClone = card.cloneNode(true);
      return cardClone instanceof HTMLElement ? [cardClone] : [];
    });
    if (this.reviewCards.length === 0) return;

    this.tabletMediaQuery = window.matchMedia('(max-width: 900px)');
    this.mobileMediaQuery = window.matchMedia('(max-width: 459px)');
    this.viewportChangeHandler = () => this.renderLayout();
    this.tabletMediaQuery.addEventListener('change', this.viewportChangeHandler);
    this.mobileMediaQuery.addEventListener('change', this.viewportChangeHandler);

    const pauseButton = this.parentElement?.querySelector('.vertical-reviews__pause-button');
    const pauseControl = pauseButton?.closest('.vertical-reviews__pause-control');
    if (pauseButton instanceof HTMLButtonElement && pauseControl instanceof HTMLElement) {
      this.pauseButton = pauseButton;
      this.pauseControl = pauseControl;
      this.pauseControl.addEventListener('click', this.playbackToggleHandler);
      this.updateControlState();
    }

    this.renderLayout();
    this.addEventListener('pointerover', this.handleCardPointerOver);
    this.addEventListener('pointerout', this.handleCardPointerOut);
  }

  disconnectedCallback() {
    if (this.viewportChangeHandler) {
      this.tabletMediaQuery?.removeEventListener('change', this.viewportChangeHandler);
      this.mobileMediaQuery?.removeEventListener('change', this.viewportChangeHandler);
    }
    this.removeEventListener('pointerover', this.handleCardPointerOver);
    this.removeEventListener('pointerout', this.handleCardPointerOut);
    this.pauseControl?.removeEventListener('click', this.playbackToggleHandler);

    if (this.playbackRateFrame) cancelAnimationFrame(this.playbackRateFrame);

    this.viewportChangeHandler = undefined;
    this.playbackRateFrame = undefined;
    this.pauseButton = undefined;
    this.pauseControl = undefined;
  }

  getLayoutMode() {
    if (this.mobileMediaQuery?.matches) return 'mobile';
    if (this.tabletMediaQuery?.matches) return 'tablet';
    return 'desktop';
  }

  renderLayout() {
    const layoutMode = this.getLayoutMode();
    if (this.dataset.layout === layoutMode) return;

    let cardsPerRow = 2;
    if (layoutMode === 'tablet') cardsPerRow = 3;
    if (layoutMode === 'mobile') cardsPerRow = 1;

    const reviewSet = document.createElement('div');
    reviewSet.className = 'vertical-reviews__set';

    let cardIndex = 0;
    let rowIndex = 0;

    while (cardIndex < this.reviewCards.length) {
      reviewSet.append(this.createRow(cardIndex, cardsPerRow));
      cardIndex += cardsPerRow;
      rowIndex += 1;
    }

    // Desktop and tablet rows alternate horizontally. Keep an even row count so
    // the cloned set begins with the same alignment and the loop stays seamless.
    if (layoutMode !== 'mobile' && rowIndex % 2 !== 0) {
      const parityRow = this.createRow(cardIndex, cardsPerRow);
      parityRow.setAttribute('aria-hidden', 'true');
      reviewSet.append(parityRow);
    }

    const duplicateSet = reviewSet.cloneNode(true);
    if (duplicateSet instanceof HTMLElement) {
      duplicateSet.setAttribute('aria-hidden', 'true');
    }

    this.replaceChildren(reviewSet, duplicateSet);
    this.dataset.layout = layoutMode;
    this.dataset.initialized = 'true';

    requestAnimationFrame(() => {
      if (this.isConnected) this.updatePlaybackState(0);
    });
  }

  /**
   * @param {number} startIndex
   * @param {number} cardCount
   * @returns {HTMLElement}
   */
  createRow(startIndex, cardCount) {
    const row = document.createElement('div');
    row.className = 'vertical-reviews__row';
    row.dataset.cardCount = String(cardCount);

    for (let offset = 0; offset < cardCount; offset += 1) {
      const sourceIndex = startIndex + offset;
      const sourceCard = this.reviewCards[sourceIndex % this.reviewCards.length];
      if (!sourceCard) continue;

      const card = sourceCard.cloneNode(true);

      if (sourceIndex >= this.reviewCards.length && card instanceof HTMLElement) {
        card.setAttribute('aria-hidden', 'true');
      }

      row.append(card);
    }

    return row;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {HTMLElement | null}
   */
  getReviewCard(target) {
    if (!(target instanceof Element)) return null;

    const card = target.closest('.vertical-reviews__card');
    return card instanceof HTMLElement && this.contains(card) ? card : null;
  }

  /**
   * @param {PointerEvent} event
   */
  handleCardPointerOver(event) {
    const card = this.getReviewCard(event.target);
    if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;

    this.isPointerPaused = true;
    this.updatePlaybackState(500);
  }

  /**
   * @param {PointerEvent} event
   */
  handleCardPointerOut(event) {
    const card = this.getReviewCard(event.target);
    if (!card || (event.relatedTarget instanceof Node && card.contains(event.relatedTarget))) return;
    if (this.getReviewCard(event.relatedTarget)) return;

    this.isPointerPaused = false;
    this.updatePlaybackState(350);
  }

  toggleUserPaused() {
    this.isUserPaused = !this.isUserPaused;
    this.updatePlaybackState(250);
  }

  /**
   * @param {number} duration
   */
  updatePlaybackState(duration) {
    const isPaused = this.isUserPaused || this.isPointerPaused;
    this.updateControlState(isPaused);
    this.easePlaybackRate(isPaused ? 0 : 1, duration);
  }

  /**
   * @param {boolean} [isPaused]
   */
  updateControlState(isPaused = false) {
    if (!this.pauseButton || !this.pauseControl) return;

    const label = isPaused ? 'Play reviews' : 'Pause reviews';
    this.pauseControl.dataset.paused = String(isPaused);
    this.pauseButton.setAttribute('aria-pressed', String(isPaused));
    this.pauseButton.setAttribute('aria-label', label);
    this.pauseButton.title = label;
  }

  /**
   * @param {number} targetRate
   * @param {number} duration
   */
  easePlaybackRate(targetRate, duration) {
    const animation = this.getAnimations().find(
      (item) => 'animationName' in item && item.animationName === 'vertical-reviews-scroll'
    );

    if (!animation) return;

    if (this.playbackRateFrame) {
      cancelAnimationFrame(this.playbackRateFrame);
    }

    if (duration === 0) {
      animation.updatePlaybackRate(targetRate);
      this.playbackRateFrame = undefined;
      return;
    }

    const startRate = animation.playbackRate;
    const startTime = performance.now();

    /**
     * @param {DOMHighResTimeStamp} currentTime
     */
    const updateRate = (currentTime) => {
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easedProgress = progress * progress * (3 - 2 * progress);
      animation.updatePlaybackRate(startRate + (targetRate - startRate) * easedProgress);

      if (progress < 1) {
        this.playbackRateFrame = requestAnimationFrame(updateRate);
      } else {
        this.playbackRateFrame = undefined;
      }
    };

    this.playbackRateFrame = requestAnimationFrame(updateRate);
  }
}

if (!customElements.get('vertical-reviews-loop')) {
  customElements.define('vertical-reviews-loop', VerticalReviewsLoop);
}
